import { AppContext } from "./AppContext";
import { useEffect, useState } from "react";
import React, {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { ethers } from "ethers";
import { useAccount, useChainId } from "wagmi";

import {
  Box,
  TabPanel,
  TabPanels,
  Tabs,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  HStack,
  Tab,
  TabList,
  Wrap,
  WrapItem,
  Image,
  Center,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { BsCurrencyExchange } from "react-icons/bs";

import NavBar from "./components/NavBar";
import { FiHome, FiBook } from "react-icons/fi";
import { IoMdAnalytics } from "react-icons/io";
import VaultPriceFeed from "./contracts/VaultPriceFeed.json";
import FooterMain from "./components/FooterMain";
import JakobWallet from "./contracts/BlindfoldWalletV2.json";
import PositionsSection from "./components/wallet/PositionsSection";
import ActiveContractInfo from "./components/ActiveContractInfo";
import BlindfoldInfo from "./components/documentation/BlindfoldInfo";
import ContractList from "./components/documentation/ContractList";
import FetchBtcPriceBase from "./components/prices/FetchBtcPriceBase";
import FetchWethPriceBase from "./components/prices/FetchWethPriceBase";
import DirectionDisplay from "./blindfold-api/DirectionDisplay";
import TradeSignalsTable from "./blindfold-api/TradeSignalsTable";
import BtcSignalsTable from "./blindfold-api/BtcSignalsTable";
import IndicatorDescription from "./blindfold-api/IndicatorDescription";
import SignalsSection from "./components/SignalsSection";
import FetchLinkPriceBase from "./components/prices/FetchLinkPriceBase";
import WebSocketPerformanceTest from "./components/WebsocketPerformanceTest";
import { alchemyRPC, meowRpc, providerUrl, weth } from "./config";
import DataBalances from "./api/DataBalances";
//import DataGrid from "./components/DataGrid";
import VideoPlayer from "./components/VideoPlayer";
import MarketSentimentCard from "./components/blindGPT/MarketSentimentGpt";
import LinkSignalsTable from "./blindfold-api/LinkSignalsTable";

const maskUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}/******`;
  } catch (error) {
    return "Invalid URL";
  }
};

function App() {
  const tabOrientation = "vertical"; // Always vertical
  const { address, isConnected } = useAccount();
  const chainId = useChainId();

  // Fastest WebSocket State
  const [fastestWebSocket, setFastestWebSocket] = useState(
    "wss://arbitrum-one-rpc.publicnode.com",
  );
  //const [loadingWebSocket, setLoadingWebSocket] = useState(true);
  const rpc42161 = providerUrl;
  const [rpcUrl, setRpcUrl] = useState(rpc42161);
  const [currentPrice, setCurrentPrice] = useState("");
  const [account, setAccount] = useState("");
  const [contractAddress, setContractAddress] = useState(JakobWallet.address);
  const walletABI = JakobWallet.abi;
  const [contractName, setContractName] = useState("");
  const [accountBalance, setAccountBalance] = useState("");
  const [totalCollateral, setTotalCollateral] = useState(0);
  const [totalPositions, setTotalPositions] = useState(0);
  const [btcPrice, setBtcPrice] = useState(null);
  const [linkPrice, setLinkPrice] = useState(null);
  const [wethPrice, setWethPrice] = useState(null);
  const [activeBalance, setActiveBalance] = useState(0);

  useEffect(() => {
    if (isConnected && 42161) {
      console.log(`Connected to chain ID: ${chainId}`);

      // ✅ Update chainId & RPC URL
      //  setChainId(42161);
      //setRpcUrl(rpc42161); // Default to Arbitrum if unknown chain

      // ✅ Set wallet address
      setAccount(address);
    }
  }, [isConnected, chainId, address]);

  // Callback function to update the BTC price
  const handleBtcPriceUpdate = (newBtcPrice) => {
    setBtcPrice(newBtcPrice);
  };

  // Callback function to update the link price
  const handleLinkPriceUpdate = (newLinkPrice) => {
    setLinkPrice(newLinkPrice);
  };

  const handleWethPriceUpdate = (newWethPrice) => {
    setWethPrice(newWethPrice);
  };

  const handleTotalCollateralChange = (collateral) => {
    setTotalCollateral(parseFloat(collateral) || 0);
  };

  const handleTotalPositionsChange = (totalPositions) => {
    setTotalPositions(parseFloat(totalPositions) || 0);
  };

  const fetchPrice = async () => {
    try {
      const provider = new ethers.providers.JsonRpcProvider(meowRpc);
      const routerContract = new ethers.Contract(
        VaultPriceFeed.address,
        VaultPriceFeed.abi,
        provider,
      );
      const tx = await routerContract.getPriceV1(weth, false, true);
      const scaleFactor = ethers.BigNumber.from("10").pow(27);
      const priceInWei = ethers.BigNumber.from(tx);
      const price = priceInWei.div(scaleFactor);
      const adjusted = Number(price.toString()) / 1000;
      setCurrentPrice(adjusted);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch contract name
  const fetchContractName = async () => {
    if (!contractAddress) return; // Skip if no contractAddress
    try {
      const provider = new ethers.providers.WebSocketProvider(fastestWebSocket);
      const contract = new ethers.Contract(
        contractAddress,
        walletABI,
        provider,
      );
      const name = await contract.ContractName();
      setContractName(name);
    } catch (error) {
      console.error("Error fetching contract info:", error);
    }
  };

  useEffect(() => {
    fetchPrice();
    if (contractAddress) {
      fetchContractName();
    }
  }, [contractAddress]);

  const handleActiveBalanceUpdate = (balance) => {
    setActiveBalance(balance);
  };

  const tradeSettings = "n/a";

  return (
    <AppContext.Provider
      value={{
        account,
        setAccount,
        chainId,
        accountBalance,
        setAccountBalance,
        walletABI,
        rpcUrl,
        rpc42161,

        contractAddress,
        setContractAddress,
        contractName,
        setContractName,
        fastestWebSocket,
      }}
    >
      <Router>
        <div
          style={{
            minHeight: "100vh",
            width: "100vw",
            maxWidth: "100vw",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: 0, flexDirection: "row" }}>
            <NavBar />
          </div>
          <Routes>
            <Route
              path="/"
              element={
                <Box
                  height="100%"
                  minHeight="100vh"
                  w="100%"
                  bg="#0F0F0F"
                  maxWidth="99vw"
                  fontFamily="Sentinel"
                  overflow="hidden"
                >
                  <HStack
                    //bg="#333333"
                    bg="#0F0F0F"
                    color="white"
                    px={{ base: 0, md: 14 }}
                    gap="auto"
                  >
                    <HStack p={1} gap="auto">
                      <Image src={"./btc.png"} w="18px" />
                      <Text fontSize="sm" color="white">
                        ${btcPrice}
                      </Text>
                    </HStack>

                    <HStack p={1} gap="auto">
                      <Image src={"./chainlink-icon.png"} w="18px" />
                      <Text fontSize="sm" color="white">
                        ${linkPrice}
                      </Text>
                    </HStack>

                    <HStack p={1} gap="auto">
                      <Image src={"./eth.png"} w="18px" />
                      <Text fontSize="sm" color="white">
                        ${wethPrice}
                      </Text>
                    </HStack>

                    <HStack gap="auto" overflow="hidden">
                      <Image src={"./blindfold-logo-white-icon.png"} w="18px" />
                      <DirectionDisplay />
                    </HStack>
                  </HStack>

                  <Tabs
                    isLazy
                    bg="#0F0F0F"
                    variant="enclosed"
                    orientation={tabOrientation}
                  >
                    <TabList
                      w="40px"
                      bg="#0F0F0F"
                      minHeight={{ base: "100vh", md: "600px" }}
                      color="white"
                      border="0"
                      gap={12}
                      flexDir="column"
                      alignItems="center"
                      py={4}
                    >
                      <TabItem icon={FiHome} label="Home" />
                      <TabItem icon={BsCurrencyExchange} label="Positions" />
                      <TabItem icon={IoMdAnalytics} label="Signals" />
                      <TabItem icon={FiBook} label="Docs" />
                    </TabList>

                    <TabPanels shadow="md" border="1.8px solid dodgerblue" bg='black'>
                      <TabPanel p={0} bg="black">
                        {/* Home Tab Content */}
                        <VStack justify="center" h="100%" mb={2}>
                          <VideoPlayer />

                          {/** GPT */}
                          <MarketSentimentCard/>
                        </VStack>
                      </TabPanel>
                      <TabPanel p={0} bg="white">
                        {contractAddress && (
                          <HStack
                            bg="white"
                            borderBottom="1px solid dodgerblue"
                            justify="left"
                            //p={1}
                            pl={2}
                            shadow="md"
                            
                          >
                            {contractAddress && <ActiveContractInfo />}
                          </HStack>
                        )}
                        <PositionsSection
                          totalCollateral={totalCollateral}
                          totalPositions={totalPositions}
                          activeBalance={activeBalance}
                          handleTotalCollateralChange={
                            handleTotalCollateralChange
                          }
                          handleTotalPositionsChange={
                            handleTotalPositionsChange
                          }
                          contractAddress={contractAddress}
                          currentPrice={currentPrice}
                          fetchPrice={fetchPrice}
                          tradeSettings={tradeSettings}
                          handleActiveBalanceUpdate={handleActiveBalanceUpdate}
                        />
                      </TabPanel>

                      <TabPanel p={0} bg="white">
                        <Wrap w="100%" h="100%">
                          <WrapItem w="100%" minWidth="300px">
                            <SignalsSection />
                          </WrapItem>
                        </Wrap>
                      </TabPanel>
                      <TabPanel p={0} h="100%" bg="white">
                        {/**Help Content */}
                        <Tabs variant="enclosed" colorScheme="blue">
                          <TabList>
                            <Tab>Blindfold</Tab>
                            <Tab>Contracts</Tab>
                            <Tab>Websockets</Tab>
                          </TabList>
                          <TabPanels>
                            <TabPanel p={4}>
                              <Box mt={8}>
                                <BlindfoldInfo />
                              </Box>
                            </TabPanel>
                            <TabPanel p={4}>
                              <Box mt={8}>
                                <ContractList />
                              </Box>
                            </TabPanel>
                            <TabPanel p={4}>
                              <Box mt={8}>
                                <WebSocketPerformanceTest
                                  setFastestWebSocket={setFastestWebSocket}
                                />
                              </Box>
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>

                  <FetchBtcPriceBase onBtcPriceUpdate={handleBtcPriceUpdate} />
                  <FetchLinkPriceBase
                    onLinkPriceUpdate={handleLinkPriceUpdate}
                  />
                  <FetchWethPriceBase
                    onWethPriceUpdate={handleWethPriceUpdate}
                  />
                </Box>
              }
            />

            <Route
              path="/ws"
              element={
                <>
                  {/** Websocket Performance */}
                  <Box p={6} bg="#DDDDDD">
                    <Box
                      bg="gray.100"
                      p={4}
                      fontSize="md"
                      textAlign="center"
                      borderRadius="md"
                      mb={3}
                      border="1px solid dodgerblue"
                    >
                      Fastest WebSocket:{" "}
                      {fastestWebSocket
                        ? maskUrl(fastestWebSocket)
                        : "Waiting for fastest connection..."}
                    </Box>
                    <WebSocketPerformanceTest
                      setFastestWebSocket={setFastestWebSocket}
                    />
                  </Box>
                </>
              }
            />

            <Route
              path={`/address/${contractAddress}`}
              element={
                <>
                  <VStack spacing={4} p={4}>
                    <Text>{contractName}</Text>
                    <Text>{contractAddress}</Text>
                    <DataBalances />
                  </VStack>
                </>
              }
            />

            <Route
              path="/signals"
              element={
                <>
                  <Text>Blindfold Composite Signals</Text>
                  <SignalsSection />
                  <IndicatorDescription />
                </>
              }
            />

            {/** ROUTE: ETH SIGNALS */}

            <Route
              path="/eth-signals"
              element={
                <>
                  {/** LINK Indicator table */}
                  <Box p={2} mb={4}>
                    <TradeSignalsTable />
                    <IndicatorDescription />
                  </Box>
                </>
              }
            />

<Route
              path="/link-signals"
              element={
                <>
                  {/** LINK Indicator table */}
                  <Box p={2} mb={4}>
                    <LinkSignalsTable/>
                    <IndicatorDescription />
                  </Box>
                </>
              }
            />

            {/** ROUTE: BTC SIGNALS */}

            <Route
              path="/btc-signals"
              element={
                <>
                  <BtcSignalsTable />
                  <IndicatorDescription />
                </>
              }
            />
          </Routes>
          <FooterMain
            style={{ flexShrink: 0, position: "sticky", bottom: 0 }}
          />
        </div>
      </Router>
    </AppContext.Provider>
  );

  function TabItem({ icon: Icon, label }) {
    return (
      <Popover trigger="hover" placement="right">
        <PopoverTrigger>
          <Tab>
            <Icon size={24} />
          </Tab>
        </PopoverTrigger>
        <PopoverContent color="black" width="fit-content" boxShadow="dark-lg">
          <PopoverBody display="flex" alignItems="center">
            <Icon size={24} style={{ marginRight: "4px" }} />
            {label}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }
}

export default App;
